import React, { useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import Avatar from '@/components/Avatar';
import { Sponsor } from '@/services/sponsorService';
import { Colors } from '@/constants/Colors';

const CARD_WIDTH = 120;
const CARD_HEIGHT = 138;
const CARD_GAP = 8;
const ANIMATION_DURATION_MS = 22_000;

interface ProfessionalDiscoveryCarouselProps {
  sponsors: Sponsor[];
  isActive: boolean;
  onPress: (sponsor: Sponsor) => void;
}

/**
 * A small, non-interruptive discovery rail for the empty appointments state.
 * The repeated row makes the end-to-start transition visually seamless.
 */
export default function ProfessionalDiscoveryCarousel({
  sponsors,
  isActive,
  onPress,
}: ProfessionalDiscoveryCarouselProps) {
  const reducedMotion = useReducedMotion();
  const translateX = useSharedValue(0);
  const carouselSponsors = useMemo(() => {
    if (sponsors.length === 0) return [];

    // Repeat the source list in complete cycles so the animation seam is
    // always between identical sequences, while keeping at least four cards.
    const cycleCount = Math.max(1, Math.ceil(4 / sponsors.length));
    const itemCount = sponsors.length * cycleCount;

    return Array.from(
      { length: itemCount },
      (_, index) => sponsors[index % sponsors.length]!,
    );
  }, [sponsors]);

  const loopDistance = carouselSponsors.length * (CARD_WIDTH + CARD_GAP);

  const repeatedSponsors = useMemo(
    () => [...carouselSponsors, ...carouselSponsors],
    [carouselSponsors],
  );

  useEffect(() => {
    cancelAnimation(translateX);

    if (!isActive || reducedMotion || loopDistance <= 0) {
      translateX.value = 0;
      return;
    }

    translateX.value = withRepeat(
      withTiming(-loopDistance, {
        duration: ANIMATION_DURATION_MS,
        easing: Easing.linear,
      }),
      -1,
      false,
    );

    return () => cancelAnimation(translateX);
  }, [isActive, loopDistance, reducedMotion, translateX]);

  const animatedRowStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  if (carouselSponsors.length === 0) return null;

  return (
    <View style={styles.viewport}>
      <Animated.View
        style={[styles.row, animatedRowStyle]}
      >
        {repeatedSponsors.map((sponsor, index) => (
          <Pressable
            key={`${sponsor.id}-${index}`}
            style={styles.card}
            onPress={() => onPress(sponsor)}
            accessibilityRole="button"
            accessibilityLabel={sponsor.name}
          >
            <Avatar
              uri={sponsor.photo_url}
              size={51}
              style={styles.avatar}
              borderWidth={0}
            />
            <View style={styles.copy}>
              <Text style={styles.name} numberOfLines={1}>
                {sponsor.name}
              </Text>
              <Text style={styles.role} numberOfLines={1}>
                {sponsor.role}
              </Text>
            </View>
          </Pressable>
        ))}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  viewport: {
    width: '100%',
    // The full-width rail is naturally clipped by the screen edges. Keeping
    // this visible is important because overflow hidden cuts card shadows.
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    alignSelf: 'flex-start',
    gap: CARD_GAP,
  },
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    padding: 20,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: Colors.light.surface,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    shadowColor: '#000000',
    shadowOffset: { width: 12, height: 20 },
    shadowOpacity: 0.04,
    shadowRadius: 16,
    elevation: 2,
  },
  avatar: {
    borderRadius: 999,
    borderColor: '#C4C2BA',
    alignSelf: 'center',
  },
  copy: {
    width: '100%',
    alignItems: 'center',
    gap: 2,
  },
  name: {
    color: Colors.light.textPrimary,
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 13,
    lineHeight: 18,
  },
  role: {
    color: '#6B9EC2',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 8,
    lineHeight: 11,
  },
});